import { useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getGroupedRowModel,
  useReactTable,
  GroupingState,
} from '@tanstack/react-table';
import Excel from 'exceljs';

import { ExportButton } from 'shared/ui';

import {
  FORMAT_TO_MINUTES,
  showTimeString,
  getRuFormatDateString,
  TABLE_PHONE_COLUMN_WIDTH,
  replaceSubstring,
  toCurrencyUnit,
  toConsumeUnit,
} from 'shared/lib';

import {
  ReportInfo,
  ReportTableLayout,
  StyledReportCard,
  ReportHeader,
  applyWorksheetStyles,
  downloadReport,
  setColumnsAutoWidth,
  setReportMetadata,
  DEFAULT_REPORT_COLUMN_WIDTH,
  type MongoAcquiringReportDTO,
  highlightLastRow,
} from 'entities/report';
import { BankDTO } from 'entities/bank';

const COLUMN = {
  psLastUpdateTime: { key: 'psLastUpdateTime', label: 'Дата оплаты' },
  userBrowseName: { key: 'userBrowseName', label: 'Пользователь' },
  calculatedPayment: { key: 'calculatedPayment', label: 'Сумма операций' },
  calculatedConsume: {
    key: 'calculatedConsume',
    label: 'Потреблено физ. лица (кВт*ч)',
  },
  cpName: { key: 'cpName', label: 'Номер ЭЗС' },
  cpAddress: { key: 'cpAddress', label: 'Адрес' },
  ownerINN: { key: 'ownerINN', label: 'ИНН Агента' },
};

const REPORT_TITLE = 'Отчет по эквайрингу';
const DATA_START_ROW = 4;

const columnHelper = createColumnHelper<MongoAcquiringReportDTO>();

const emptyArr: MongoAcquiringReportDTO[] = [];

type Props = {
  banks: BankDTO[];
  sessions: MongoAcquiringReportDTO[];
  loading: boolean;
};

export function AcquiringReportTable({ banks, sessions, loading }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  const dateFromParam = searchParams.get('dateFrom');
  const dateToParam = searchParams.get('dateTo');
  const bankIdParam = searchParams.get('bankId');

  const currencyText = useMemo(() => {
    const text = banks.find((bank) => String(bank.bankId) === bankIdParam)
      ?.country.currencyText;

    return text ? `(${text})` : '';
  }, [bankIdParam]);

  const [grouping, setGrouping] = useState<GroupingState>([]);

  const generateTable = async () => {
    const workbook = new Excel.Workbook();

    setReportMetadata(workbook);

    const worksheet = workbook.addWorksheet(REPORT_TITLE);

    worksheet.mergeCells('A1', 'G1');

    worksheet.getCell('A2').value = 'Отчетный период:';
    worksheet.mergeCells('B2', 'G2');
    worksheet.getCell('B2').value = `${getRuFormatDateString(
      dateFromParam as string
    )}-${getRuFormatDateString(dateToParam as string)}`;

    worksheet.getRow(DATA_START_ROW).values = [
      COLUMN.psLastUpdateTime.label,
      COLUMN.userBrowseName.label,
      `${COLUMN.calculatedPayment.label} ${currencyText}`,
      COLUMN.calculatedConsume.label,
      COLUMN.cpName.label,
      COLUMN.cpAddress.label,
      COLUMN.ownerINN.label,
    ];

    worksheet.columns = [
      { key: COLUMN.psLastUpdateTime.key, width: DEFAULT_REPORT_COLUMN_WIDTH },
      { key: COLUMN.userBrowseName.key, width: DEFAULT_REPORT_COLUMN_WIDTH },
      { key: COLUMN.calculatedPayment.key, width: DEFAULT_REPORT_COLUMN_WIDTH },
      { key: COLUMN.calculatedConsume.key, width: DEFAULT_REPORT_COLUMN_WIDTH },
      { key: COLUMN.cpName.key, width: DEFAULT_REPORT_COLUMN_WIDTH },
      { key: COLUMN.cpAddress.key, width: DEFAULT_REPORT_COLUMN_WIDTH },
      { key: COLUMN.ownerINN.key, width: DEFAULT_REPORT_COLUMN_WIDTH },
    ];

    sessions.forEach(
      ({
        psLastUpdateTime,
        userBrowseName,
        operation: { calculatedPayment, calculatedConsume, cpAddress, cpName },
        ownerINN,
      }) => {
        worksheet.addRow({
          psLastUpdateTime: replaceSubstring(
            showTimeString(psLastUpdateTime, FORMAT_TO_MINUTES),
            '-',
            '.'
          ),
          userBrowseName,
          calculatedPayment: replaceSubstring(
            toCurrencyUnit(calculatedPayment),
            '.',
            ','
          ),
          calculatedConsume: replaceSubstring(
            toConsumeUnit(calculatedConsume),
            '.',
            ','
          ),
          cpName,
          cpAddress,
          ownerINN: ownerINN ?? '',
        });
      }
    );

    worksheet.addRow({
      psLastUpdateTime: 'Итого:',
      userBrowseName: '',
      calculatedPayment: replaceSubstring(paymentSummary, '.', ','),
      calculatedConsume: replaceSubstring(consumeSummary, '.', ','),
      cpName: `Всего операций: ${operationsSummary}`,
      cpAddress: '',
      ownerINN: '',
    });

    applyWorksheetStyles(worksheet, DATA_START_ROW);
    highlightLastRow(worksheet);

    const lastRowIndex = sessions.length + DATA_START_ROW + 1;

    worksheet.mergeCells(`A${lastRowIndex}:B${lastRowIndex}`);
    worksheet.mergeCells(`E${lastRowIndex}:G${lastRowIndex}`);

    worksheet.getCell('A1').value = REPORT_TITLE;
    worksheet.getCell('B2').alignment = {
      horizontal: 'left',
    };

    setColumnsAutoWidth(worksheet, DATA_START_ROW);

    const reportFileName = `${REPORT_TITLE}-${dateFromParam}-${dateToParam}`;

    downloadReport(workbook, reportFileName);
  };

  const paymentSummary = toCurrencyUnit(
    sessions.reduce((acc, el) => {
      return acc + el.operation.calculatedPayment;
    }, 0)
  );

  const consumeSummary = toConsumeUnit(
    sessions.reduce((acc, el) => {
      return acc + el.operation.calculatedConsume;
    }, 0)
  );

  const operationsSummary = sessions.length;

  const defaultColumns = [
    columnHelper.group({
      header: 'Итого',
      footer: (props) => {
        return 'Итого';
      },
      columns: [
        columnHelper.accessor('psLastUpdateTime', {
          header: COLUMN.psLastUpdateTime.label,
          id: 'psLastUpdateTime',
          cell: (props) =>
            showTimeString(
              props.row.original.psLastUpdateTime,
              FORMAT_TO_MINUTES
            ),
          size: 155,
        }),
        columnHelper.accessor('userBrowseName', {
          header: COLUMN.userBrowseName.label,
          id: 'userBrowseName',
          size: TABLE_PHONE_COLUMN_WIDTH,
        }),
      ],
    }),
    columnHelper.group({
      header: 'calculatedPayment',
      footer: paymentSummary,
      columns: [
        columnHelper.accessor('operation', {
          header: `${COLUMN.calculatedPayment.label} ${currencyText}`,
          id: 'operation.calculatedPayment',
          cell: (props) =>
            toCurrencyUnit(props.row.original.operation.calculatedPayment),
        }),
      ],
    }),
    columnHelper.group({
      header: 'calculatedConsume',
      footer: consumeSummary,
      columns: [
        columnHelper.accessor('operation', {
          header: COLUMN.calculatedConsume.label,
          id: 'operation.calculatedConsume',
          cell: (props) =>
            toConsumeUnit(props.row.original.operation.calculatedConsume),
        }),
      ],
    }),
    columnHelper.group({
      header: 'Operations',
      footer: (props) => {
        return `Всего операций: ${operationsSummary}`;
      },
      columns: [
        columnHelper.accessor('operation', {
          header: COLUMN.cpName.label,
          id: 'operation.cpName',
          cell: (props) => props.row.original.operation.cpName,
        }),
        columnHelper.accessor('operation', {
          header: COLUMN.cpAddress.label,
          id: 'operation.cpAddress',
          cell: (props) => props.row.original.operation.cpAddress,
        }),
        columnHelper.accessor('ownerINN', {
          header: COLUMN.ownerINN.label,
          id: 'ownerINN',
          cell: (props) => props.row.original.ownerINN,
        }),
      ],
    }),
  ];

  const columns = useMemo(
    () => defaultColumns,
    [paymentSummary, consumeSummary, operationsSummary, currencyText]
  );

  const data = useMemo(() => {
    return sessions ?? emptyArr;
  }, [sessions]);

  const table = useReactTable({
    data,
    columns,
    state: {
      grouping,
    },
    onGroupingChange: setGrouping,
    getGroupedRowModel: getGroupedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <StyledReportCard>
      <ReportHeader>
        <ReportInfo
          title={REPORT_TITLE}
          dateFromParam={dateFromParam}
          dateToParam={dateToParam}
        />
        <ExportButton
          onClick={generateTable}
          disabled={loading || !sessions.length}
        />
      </ReportHeader>
      <ReportTableLayout
        table={table}
        loading={loading}
        ignoreHeaderGroups={['0']}
        includeFooterGroups={['0']}
      />
    </StyledReportCard>
  );
}
